import { useEffect } from 'react';
import { ProgramListFilter } from '@widgets/programs/index.ts';
import { ProgramListFilterProps } from '@widgets/programs/list-filter/ui.tsx';
import { InfinityList } from '@widgets/shared/InfinityList/index.tsx';
import { ArticleCardSmall } from '@features/articles/article-card/Small/index.tsx';
import { BodyWithFilterAndList } from '@features/common/BodyWithFilterAndList/index.tsx';
import { useMobile } from '@shared/hooks/index.ts';
import { useListTabs } from '@shared/hooks/use-list-tabs.ts';
import { useSaveScroll } from '@shared/hooks/use-save-scroll.ts';
import type { ActiveTabType } from '@shared/types/common';
import { SkeletonList } from '@shared/ui/skeleton-list/ui.tsx';
import { useArticlesInfiniteQuery } from '@src/api/api-hooks/articles/useArticlesInfiniteQuery.ts';
import { Blank } from '@src/library/components/common/index.ts';
import * as st from './styles.module.scss';

interface Props {
  onTitlesChange: (titles: string[]) => void;
  filterData: Omit<ProgramListFilterProps, 'activeTab'>;
  activeTab?: ActiveTabType;
  search: string;
  isFilterActive: boolean;
}

export const ArticlesListBody: React.FC<Props> = ({
  filterData,
  onTitlesChange,
  search,
  isFilterActive,
}) => {
  const { isMobile } = useMobile();
  const { setListScrollData } = useSaveScroll('ArticlesListScrollData');
  const { selectedTags, selectedPartitions, selectedAuthors } = filterData;
  const { activeTab } = useListTabs();
  const { isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, data } =
    useArticlesInfiniteQuery({
      partitions: selectedPartitions?.length ? selectedPartitions.join(',') : undefined,
      tags: selectedTags?.length ? selectedTags.join(',') : undefined,
      authors: selectedAuthors?.length ? selectedAuthors.join(',') : undefined,
      search: search.length ? search : undefined,
      tabName: activeTab,
    });

  useEffect(() => {
    if (data) {
      onTitlesChange(data.results.map((el) => el.title));
    }
  }, [data, onTitlesChange]);

  return (
    <BodyWithFilterAndList
      isFilterOpen={filterData.isOpen}
      Filter={<ProgramListFilter {...filterData} activeTab={activeTab} />}
      List={
        <InfinityList
          className={st.list}
          loading={isFetchingNextPage}
          loadMore={fetchNextPage}
          hasMore={hasNextPage}
        >
          {isLoading ? (
            <SkeletonList />
          ) : (
            data?.results.map((article, index) => (
              <ArticleCardSmall
                needDropDawnShowUp={
                  index === data?.results.length - 1 &&
                  (isMobile ? data?.results.length > 1 : data?.results.length > 6)
                }
                onRedirect={() =>
                  setListScrollData({
                    scrollY: window.scrollY,
                  })
                }
                activeTab={activeTab}
                article={article}
                key={article.id}
              />
            ))
          )}
        </InfinityList>
      }
      isShowBlank={!isLoading && !data?.results.length}
      EmptyBlank={
        <Blank
          text={isFilterActive ? 'По вашему запросу ничего не найдено' : 'В этом списке ничего нет'}
        />
      }
    />
  );
};
